import * as preact from "preact";
import * as _ from "underscore";

import * as accountModal from "accountModal";
import * as localAPI from "localAPI";
import models from "models/models";
import readlangClient from "readlangClient";
import * as languageMap from "shared/languageMap";
import * as signupModal from "signupModal";
import LanguageSelectorView from "views/LanguageSelectorView";

type Props = {
  appRouter: any;
};

type State = {
  genreType:
    | "classic-fiction"
    | "non-fiction"
    | "biography"
    | "original-fiction";
  topic: string;
  level: "beginner" | "intermediate" | "advanced";
  length: number;

  showLanguageSelector?: boolean;

  generating: boolean;
  generatedStory?: string;
  generatingComplete: boolean;
};

const genres: {
  type: State["genreType"];
  name: string;
  topicName: string;
  topicDescription: string;
  topicPlaceholder: string;
}[] = [
  {
    type: "classic-fiction",
    name: "Classic Fiction",
    topicName: "Name of Story",
    topicDescription:
      "Enter the name of a widely known classic story or fairy tale, e.g. The Three Little Pigs, Oliver Twist, The Iliad, etc...",
    topicPlaceholder: "Type name of story",
  },
  {
    type: "non-fiction",
    name: "Non-Fiction",
    topicName: "Topic",
    topicDescription:
      "Enter a widely known non-fiction topic to simplify, e.g. The History of the Internet, Word War 2, Particle Physics, etc...",
    topicPlaceholder: "Type topic",
  },
  {
    type: "biography",
    name: "Biography",
    topicName: "Person",
    topicDescription:
      "Enter the name of a widely known person whose story you want to write, e.g. Albert Einstein, Marie Curie, Simón Bolívar etc...",
    topicPlaceholder: "Type name of person",
  },
  {
    type: "original-fiction",
    name: "Original Fiction",
    topicName: "Idea",
    topicDescription:
      "Enter an idea for the story you want to write, e.g. a haunted house, a love story on a spaceship, etc...",
    topicPlaceholder: "Type idea",
  },
];

const genresMap = _.indexBy(genres, "type");

class StoryBotPageView extends preact.Component<Props, State> {
  languageSelectorElement: HTMLElement;

  constructor(props: Props) {
    super(props);

    this.state = {
      genreType: "classic-fiction",
      topic: "",
      level: "beginner",
      length: 1000,

      generatingComplete: false,
      generating: false,
    };

    models.user.on("change", () => this.forceUpdate());

    // Re-fetch user to ensure we have up-to-date data
    models.user.fetch();
    document.addEventListener("click", this.onClickOutsideLanguageSelector);
  }

  onClickOutsideLanguageSelector = (event: MouseEvent) => {
    if (
      this.state.showLanguageSelector &&
      !event.composedPath().includes(this.languageSelectorElement)
    ) {
      this.setState({ showLanguageSelector: false });
    }
  };

  componentWillUnmount() {
    document.removeEventListener("click", this.onClickOutsideLanguageSelector);
  }

  render() {
    const languageString =
      languageMap.map[models.user.get("currentLearningLanguage")].english;

    const genre = genres.find(
      (potentialGenre) => potentialGenre.type === this.state.genreType
    );

    return (
      <div class="storyBotContainer">
        <img class="storyBotImage" src="/content/storyBot.webp" />

        <div class="topSection">
          <h2>Readlang Story Bot</h2>

          <p>
            Ask the Readlang Story Bot (powered by gpt-4o) to write you a story
            or article to practice reading {languageString}.
          </p>
          <p>
            All generated texts will be shared on the{" "}
            <a href="/library">{languageString} public library</a>.
          </p>
        </div>

        <table>
          <tr>
            <td>Language</td>
            <td class="languageSelectorContainer">
              {this.state.generating ? (
                languageString
              ) : (
                <>
                  <span
                    onClick={(event) => {
                      // Stop propagation so that it doesn't trigger the click handler on the whole
                      // document used to dismiss the dropdown
                      event.stopPropagation();
                      this.setState({ showLanguageSelector: true });
                    }}
                  >
                    <select style={{ pointerEvents: "none" }}>
                      <option>{languageString}</option>
                    </select>
                  </span>
                  {this.state.showLanguageSelector ? (
                    <div
                      style={{
                        position: "absolute",
                        background: "white",
                        top: "0",
                        left: "0",
                        zIndex: 10,
                        padding: "10px",
                        border: "1px solid #ccc",
                        borderRadius: "5px",
                      }}
                      ref={(element) =>
                        (this.languageSelectorElement = element)
                      }
                    >
                      <h3 style={{ marginBottom: "10px" }}>Select Language</h3>
                      <LanguageSelectorView
                        featuredLanguages={models.user.get("activeLanguages")}
                        languageType="learning"
                        setLanguage={(languageCode: string) => {
                          this.setState({ showLanguageSelector: false });
                          models.user.save(
                            { currentLearningLanguage: languageCode },
                            { patch: true }
                          );
                        }}
                      />
                    </div>
                  ) : null}
                </>
              )}
              {/* <select value={this.state.genreType}>
                {genres.map((thisGenre) => (
                  <option value={thisGenre.type}>{thisGenre.name}</option>
                ))}
              </select> */}
            </td>
          </tr>
          <tr>
            <td>Genre</td>
            <td>
              <select
                value={this.state.genreType}
                onChange={(event) => {
                  this.setState({
                    genreType: event.currentTarget.value as State["genreType"],
                  });
                }}
                disabled={this.state.generating}
              >
                {genres.map((thisGenre) => (
                  <option value={thisGenre.type}>{thisGenre.name}</option>
                ))}
              </select>
              <br />
              {genre.topicDescription}
            </td>
          </tr>
          <tr>
            <td>{genre.topicName}</td>
            <td>
              <input
                type="text"
                value={this.state.topic}
                placeholder={genre.topicPlaceholder}
                onInput={(event) =>
                  this.setState({ topic: event.currentTarget.value })
                }
                disabled={this.state.generating}
              />
            </td>
          </tr>
          <tr>
            <td>Reading Level</td>
            <td>
              <select
                value={this.state.level}
                onChange={(event) =>
                  this.setState({
                    level: event.currentTarget.value as State["level"],
                  })
                }
                disabled={this.state.generating}
              >
                <option value="beginner">Beginner</option>
                <option value="intermetiate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>
            </td>
          </tr>
          <tr>
            <td>
              Length <small>(approximate)</small>
            </td>
            <td>
              <select
                value={this.state.length}
                onChange={(event) =>
                  this.setState({
                    length: parseInt(event.currentTarget.value, 10),
                  })
                }
                disabled={this.state.generating}
              >
                <option value={300}>300 words</option>
                {/* Intentionally displaying a different number to the user since gpt-4o is
                 consistently writing stories with fewer than 1000 words. */}
                <option value={1000}>800 words</option>
              </select>
            </td>
          </tr>
          <tr>
            <td colSpan={2}>
              {this.state.generating ? (
                this.state.generatedStory ? (
                  `Generated ${this.state.generatedStory.split(" ").length} words`
                ) : (
                  `Generating...`
                )
              ) : (
                <button
                  class="generateButton prominent"
                  onClick={() => this.generateStory()}
                >
                  Generate Story
                </button>
              )}
            </td>
          </tr>
        </table>

        <p>
          Visit{" "}
          <a href="https://forum.readlang.com/t/new-story-bot-for-ai-generated-stories/1309">
            this forum thread
          </a>{" "}
          to leave feedback about the Readlang Story Bot.
        </p>
      </div>
    );
  }

  generateStory = async () => {
    if (await localAPI.isLocalMode()) {
      console.log("local api");
      signupModal.show({
        headingHtml: "<h2>Sign in and upgrade to use Story Bot</h2>",
        models: models,
      });
      return;
    }
    if (!models.user.hasPremiumAccess()) {
      accountModal.show({
        model: models.user,
        title: "storyBot",
        messageHtml: "<h2>Upgrade to use the Story Bot</h2>",
        showTitle: false,
      });
      return;
    }

    if (this.state.topic.trim() === "") {
      alert(`Please enter ${genresMap[this.state.genreType].topicName}`);
      return;
    }

    this.setState({ generating: true });

    fetch(
      `/api/storyBot/generate?genre=${encodeURIComponent(
        this.state.genreType
      )}&topic=${encodeURIComponent(
        this.state.topic.trim()
      )}&length=${encodeURIComponent(
        this.state.length
      )}&level=${encodeURIComponent(
        this.state.level
      )}&language=${encodeURIComponent(models.user.get("currentLearningLanguage"))}`,
      {
        method: "GET",
      }
    )
      .then(async (response) => {
        if (response?.ok === false) {
          if (response.status === 403) {
            // To be sure that the user has reached the limit, we could also check the response
            // text, but for simplicity we're going to assume that a 403 here means that they've
            // hit the daily limit.
            alert("Not permitted");
          }
          const text = await response.text();
          if (response.status === 400 && text === "bad-topic") {
            this.setState({ generating: false });
            switch (this.state.genreType) {
              case "classic-fiction":
                alert(
                  `Readlang Story Bot does not recognize the story "${this.state.topic}"`
                );
                return;

              case "original-fiction":
                alert(
                  `Readlang Story Bot refuses to write a story about "${this.state.topic}"`
                );
                return;

              case "non-fiction":
                alert(
                  `Readlang Story Bot doesn't know enough to write about "${this.state.topic}"`
                );
                return;

              case "biography":
                alert(
                  `Readlang Story Bot is not sure who "${this.state.topic}" is`
                );
                return;
            }
          }
          alert("Bad request: " + response.status + " " + text);
          return;
        }

        const reader = response.body.getReader();
        const processStream = () =>
          reader.read().then(async ({ done, value }) => {
            if (done) {
              this.setState({ generatingComplete: true });

              const { data } = await readlangClient.request(
                "GET",
                "/api/storyBot/title",
                {
                  genre: this.state.genreType,
                  topic: this.state.topic,
                  snippet: this.state.generatedStory.substring(0, 300),
                  language: models.user.get("currentLearningLanguage"),
                }
              );

              // Upload book to library
              readlangClient.request(
                "POST",
                "/api/book",
                {
                  title: data.title,
                  author: "Readlang Story Bot",
                  category: (() => {
                    switch (this.state.genreType) {
                      case "classic-fiction":
                      case "original-fiction":
                        return "Fiction";

                      case "non-fiction":
                      case "biography":
                        return "Non-Fiction";
                    }
                  })(),
                  language: models.user.get("currentLearningLanguage"),
                  useEveryNewLine: true,
                  public: true,
                  source: "storyBot",
                  body: this.state.generatedStory,
                },
                (responseData) => {
                  this.props.appRouter.navigate(
                    `/library/${responseData.bookID}`,
                    { trigger: true }
                  );
                }
              );

              return;
            }

            // Decode the received bytes and update the UI
            const decoded = new TextDecoder("utf-8").decode(value);
            if (decoded !== undefined) {
              await this.setState({
                generatedStory: (this.state.generatedStory ?? "") + decoded,
              });
            }

            // Continue processing the stream
            return processStream();
          });

        return processStream();
      })
      .catch((error) => {
        alert("ERROR: " + error);
      });
  };
}

export default StoryBotPageView;
